import type { Log } from '../types/log';
import { store } from '../store';
import { setViewMode, setSelectedPet, setSelectedIssueType, setSelectedTimestamp, setVisiblePanels } from '../store/slices/dashboardSlice';
import type { DashboardPanelId } from '../store/slices/dashboardSlice';

const ISSUE_TYPE_MAP: Record<string, string> = {
  general: 'behavior-change',
  meeting: 'behavior-change',
  deadline: 'behavior-change',
  todo: 'heart-rate-spike',
  reminder: 'breathing-irregular',
};

export const ISSUE_TYPE_PANEL_MAP: Record<string, DashboardPanelId[]> = {
  'heart-rate-spike': ['heartRate', 'breathRate', 'thermal'],
  'breathing-irregular': ['breathRate'],
  'temperature-abnormal': ['thermal'],
  'pain-assessment': ['petDetection', 'images'],
  'gait-abnormality': ['petDetection', 'images'],
  'wound-detected': ['petDetection', 'images'],
};

export const ISSUE_TYPE_VIDEO_MAP: Record<string, Record<string, string>> = {
  'pain-assessment': {
    images: '/videos/out_cat_pain.mp4',
  },
  'gait-abnormality': {
    images: '/videos/Dog_spot_movement.mp4',
  },
  'wound-detected': {
    images: '/videos/out_wound.mp4',
  },
};

export const ISSUE_TYPES = [
  { id: 'heart-rate-spike', name: 'Heart Rate Spike' },
  { id: 'breathing-irregular', name: 'Breathing Irregular' },
  { id: 'temperature-abnormal', name: 'Abnormal Temperature' },
  { id: 'pain-assessment', name: 'Pain Assessment (Cat)' },
  { id: 'gait-abnormality', name: 'Gait Abnormality (Dog)' },
  { id: 'wound-detected', name: 'Wound / Trauma' },
];

export function generatePlaybackUrl(log: Log): string | null {
  if (!log.startTime) return null;

  const metadata = log.metadata as Record<string, unknown> | undefined;
  const deviceName = (metadata?.deviceName as string) || (log.subjects?.[0]?.name) || '';
  const issueType = (metadata?.issueType as string) || ISSUE_TYPE_MAP[log.category] || log.category;
  const timestamp = new Date(log.startTime).toISOString();
  const panels = ISSUE_TYPE_PANEL_MAP[issueType] || ['heartRate', 'breathRate', 'thermal', 'petDetection'];

  const params = new URLSearchParams({
    mode: 'playback',
    pet: deviceName,
    type: issueType,
    timestamp,
    panels: panels.join(','),
    autoplay: 'true',
  });

  return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
}

export function navigateToPlayback(log: Log): void {
  if (!log.startTime) {
    console.warn('[playbackUrl] Cannot navigate to playback: log has no startTime');
    return;
  }

  const metadata = log.metadata as Record<string, unknown> | undefined;
  const deviceName = (metadata?.deviceName as string) || (log.subjects?.[0]?.name) || '';
  const issueType = (metadata?.issueType as string) || ISSUE_TYPE_MAP[log.category] || log.category;
  const timestamp = new Date(log.startTime).toISOString();
  const panels = ISSUE_TYPE_PANEL_MAP[issueType] || ['heartRate', 'breathRate', 'thermal', 'petDetection'];

  store.dispatch(setViewMode('playback'));
  if (deviceName) store.dispatch(setSelectedPet(deviceName));
  store.dispatch(setSelectedIssueType(issueType));
  store.dispatch(setSelectedTimestamp(timestamp));
  store.dispatch(setVisiblePanels(panels));

  window.dispatchEvent(new CustomEvent('navigate-to-playback'));
}